// TrueUpTab.tsx
import {
  useTrueUpCases,
  useTrueUpSummary,
} from "@features/bx/BulkBillGeneration/hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { GenericTable } from "@shared/components/GenericTable";
import { useToast } from "@shared/hooks/use-toast";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  ArrowDown,
  ArrowUp,
  Calculator,
  HelpCircle,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TrueUpCalculationDialog } from "./TrueUpCalculationDialog";
import { TrueUpFilters } from "./TrueUpFilters";
import { useCurrency } from "@shared/selectors/globalSelectors";

// Interface for True-Up data
interface TrueUpBilling {
  id: string | number;
  consumerNo: string;
  name: string;
  billDays: number;
  estimatedKwh: number;
  actualKwh: number;
  variance: number;
  variancePercent: string;
  estimatedBill: number;
  actualBill: number;
  adjustment: number;
  status: "Exception" | "Under-Billed" | "Over-Billed" | "Normal";
}

export const TrueUpTab: React.FC = () => {
  const { toast } = useToast();
  const currency = useCurrency();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
  });
  const [isCalculationModalOpen, setIsCalculationModalOpen] = useState(false);
  const itemsPerPage = PAGE_SIZE;
  const { cycleId } = useParams();

  // Build query parameters for the hook
  const queryParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      limit: itemsPerPage,
    };

    if (searchTerm.trim()) {
      params.search = searchTerm;
    }

    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }

    return params;
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const { data: trueUpCases, isLoading } = useTrueUpCases(
    cycleId,
    queryParams.page,
    queryParams.limit,
    queryParams.search,
    queryParams.status,
  );

  const { data: trueUpSummary } = useTrueUpSummary(cycleId);

  // Calculate KPI metrics
  const kpiMetrics = useMemo(() => {
    const totalCases = trueUpSummary?.totalCases ?? "NA";
    const overBilled = trueUpSummary?.overBilled ?? "NA";
    const underBilled = trueUpSummary?.underBilled ?? "NA";
    const netImpact = trueUpSummary?.netImpact ?? "NA";

    return {
      totalCases,
      overBilled,
      underBilled,
      netImpact,
    };
  }, [trueUpSummary]);

  // Pagination calculations
  const totalItems = trueUpCases?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: { status: string }) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({ status: "all" });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Handle export
  const handleExport = () => {
    console.log("Exporting True-Up billing report...");
    toast({
      title: "Export Started",
      description: "Your report is being generated...",
      variant: "default",
    });
  };

  // Get status badge configuration
  const getStatusBadgeConfig = (status: string) => {
    switch (status) {
      case "Exception":
        return {
          className: "bg-red-100 text-red-800 border-red-200",
          label: "Exception",
        };
      case "Under-Billed":
        return {
          className: "bg-green-100 text-green-800 border-green-200",
          label: "Under-Billed",
        };
      case "Over-Billed":
        return {
          className: "bg-orange-100 text-orange-800 border-orange-200",
          label: "Over-Billed",
        };
      case "Normal":
        return {
          className: "bg-blue-100 text-blue-800 border-blue-200",
          label: "Normal",
        };
      default:
        return {
          className: "bg-gray-100 text-gray-800 border-gray-200",
          label: status,
        };
    }
  };

  // Get variance badge configuration
  const getVarianceBadgeConfig = (
    variance: number,
    variancePercent: string,
  ) => {
    const isPositive = variance > 0;
    const isHighVariance = Math.abs(variance) > 100;

    if (isHighVariance) {
      return {
        className: isPositive
          ? "bg-red-100 text-red-800 border-red-200 font-semibold"
          : "bg-orange-100 text-orange-800 border-orange-200 font-semibold",
        icon: isPositive ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        ),
      };
    }

    return {
      className: isPositive
        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
        : "bg-gray-100 text-gray-800 border-gray-200",
      icon: isPositive ? (
        <ArrowUp className="h-3 w-3" />
      ) : (
        <ArrowDown className="h-3 w-3" />
      ),
    };
  };

  // Define table columns
  const columns: TableColumn<TrueUpBilling>[] = [
    {
      key: "consumerNo",
      header: "Consumer No.",
      type: "text",
      sortable: false,
    },
    {
      key: "name",
      header: "Name",
      type: "text",
      sortable: false,
    },
    {
      key: "billDays",
      header: "Bill Days",
      type: "text",
      sortable: false,
    },
    {
      key: "estimatedKwh",
      header: "Estimated (kWh)",
      type: "text",
      sortable: false,
    },
    {
      key: "actualKwh",
      header: "Actual (kWh)",
      type: "text",
      sortable: false,
    },
    {
      key: "variancePercent",
      header: "Variance",
      type: "custom",
      sortable: false,
      render: (value: string, row: TrueUpBilling) => {
        const config = getVarianceBadgeConfig(row.variance, value);
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${config.className}`}
          >
            {config.icon}
            {value}
          </span>
        );
      },
    },
    {
      key: "estimatedBill",
      header: "Estimated Bill",
      type: "custom",
      sortable: false,
      render: (value: number) => `₹${value.toLocaleString()}`,
    },
    {
      key: "actualBill",
      header: "Actual Bill",
      type: "custom",
      sortable: false,
      render: (value: number) => `₹${value.toLocaleString()}`,
    },
    {
      key: "adjustment",
      header: "Adjustment",
      type: "custom",
      sortable: false,
      render: (value: number) => {
        const isPositive = value > 0;
        return (
          <span
            className={`font-medium ${
              isPositive ? "text-green-600" : "text-red-600"
            }`}
          >
            {isPositive ? "+" : ""}₹{Math.abs(value).toLocaleString()}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      sortable: false,
      render: (value: string) => {
        const config = getStatusBadgeConfig(value);
        return (
          <span
            className={`inline-flex px-2 py-1 rounded border text-xs font-medium ${config.className}`}
          >
            {config.label}
          </span>
        );
      },
    },
  ];

  // KPI Cards data
  const kpiCards = [
    {
      title: "Total True-Up Cases",
      count: kpiMetrics.totalCases,
      trendSummary: "Requiring reconciliation",
      icon: <Calculator className="h-8 w-8 text-purple-600" />,
    },
    {
      title: "Over-Billed",
      count: kpiMetrics.overBilled,
      trendSummary: "Credit adjustments needed",
      icon: <TrendingDown className="h-8 w-8 text-red-500" />,
    },
    {
      title: "Under-Billed",
      count: kpiMetrics.underBilled,
      trendSummary: "Additional charges due",
      icon: <TrendingUp className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Net Impact",
      count: `${currency}${(kpiMetrics.netImpact / 100000).toFixed(1)}L`,
      displayValue: `${currency}${(kpiMetrics.netImpact / 100000).toFixed(1)}L`,
      trendSummary: "Additional revenue",
      icon: <span className="text-2xl">{currency}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-start justify-between p-4 bg-purple-50 border-l-4 border-purple-600 rounded-r-lg">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-purple-900 mb-2">
            True-Up Billing Summary
          </h2>
          <p className="text-sm text-purple-800">
            True-Up billing reconciles consumption differences between estimated
            and actual meter readings. Review adjustments, exceptions, and
            billing impacts below.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCalculationModalOpen(true)}
        >
          <HelpCircle className="h-4 w-4" />
          How True-Up Bill is Calculated
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            displayValue={card.displayValue}
            trendSummary={card.trendSummary}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Table Section with Integrated Filters */}
      <GenericTable
        data={trueUpCases?.results || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        selectable={false}
        searchPlaceholder="Search by name or consumer no..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        filterPanel={
          <TrueUpFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />
        }
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
        onExport={handleExport}
        exportLoading={false}
        emptyMessage="No true-up cases found"
      />

      {/* Calculation Dialog */}
      <TrueUpCalculationDialog
        open={isCalculationModalOpen}
        onOpenChange={setIsCalculationModalOpen}
      />
    </div>
  );
};
